import { create } from 'zustand';
import type { Session, User } from '@supabase/supabase-js';
import type { Tables } from '@fluent/types';

/**
 * Single source of truth for auth state. AuthProvider subscribes to
 * Supabase `onAuthStateChange` and syncs into this store — everything
 * else reads via `useAuth()`.
 */

export type Profile = Tables<'profiles'>;

interface AuthState {
  /** undefined while we're still resolving the initial session from storage. */
  session: Session | null | undefined;
  user: User | null;
  profile: Profile | null;
  profileLoading: boolean;

  setSession: (session: Session | null) => void;
  setProfile: (profile: Profile | null) => void;
  setProfileLoading: (loading: boolean) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  session: undefined,
  user: null,
  profile: null,
  profileLoading: false,

  setSession: (session) =>
    set({
      session,
      user: session?.user ?? null,
      // profile is fetched separately; clearing session clears profile too
      ...(session ? {} : { profile: null }),
    }),
  setProfile: (profile) => set({ profile }),
  setProfileLoading: (loading) => set({ profileLoading: loading }),
  clear: () => set({ session: null, user: null, profile: null }),
}));

/**
 * Convenience hook — the most common read shape.
 * `authReady` is true once we've resolved the initial session (even if null).
 */
export function useAuth() {
  const session = useAuthStore((s) => s.session);
  const user = useAuthStore((s) => s.user);
  const profile = useAuthStore((s) => s.profile);
  const profileLoading = useAuthStore((s) => s.profileLoading);
  return {
    session,
    user,
    profile,
    profileLoading,
    authReady: session !== undefined,
    isAuthenticated: Boolean(session),
  };
}
